// Publications.js
import React, { useState } from 'react';
import { fadeInUp } from 'react-animations';
import { StyleSheet, css } from 'aphrodite';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExternalLinkAlt, faCalendarAlt, faBook, faHeadphones, faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import AudioPlayer from './AudioPlayer';

function Publications({ title, imageUrl, description, link, authors, journal, type, keywords, date, abstract, schemaData, podcasts = [] }) {
  const [audioExpanded, setAudioExpanded] = useState(false);
  // Helper to bold user name in author list
  const highlightAuthor = (authors) => {
    if (!authors) return null;
    return authors.map((author, idx) =>
      author.toLowerCase().includes('mohammad sayem chowdhury') ?
        <strong key={idx} style={{ color: 'var(--primary)' }}>{author}</strong> :
        <span key={idx}>{author}</span>
    ).reduce((prev, curr) => [prev, ', ', curr]);
  };

  const styles = StyleSheet.create({
    fadeInUp: {
      animationName: fadeInUp,
      animationDuration: '1s'
    },
    publicationCard: {
      background: 'var(--bg-card)',
      borderRadius: '8px',
      padding: '28px',
      border: '1px solid var(--border)',
      transition: 'border-color 0.2s ease',
      display: 'flex',
      flexDirection: 'row',
      gap: '24px',
      ':hover': {
        borderColor: 'var(--primary)'
      },
      '@media (max-width: 900px)': {
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '16px',
        padding: '20px',
      }
    },
    imageSection: {
      width: '80px',
      height: '100px',
      borderRadius: '4px',
      overflow: 'hidden',
      flexShrink: 0,
      marginRight: '16px',
      opacity: 0.7,
      '@media (max-width: 900px)': {
        width: '60px',
        height: '75px',
        marginRight: 0,
        marginBottom: '8px',
      }
    },
    contentSection: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      '@media (max-width: 900px)': {
        width: '100%',
        alignItems: 'flex-start',
      }
    },
    title: {
      fontSize: '1.15rem',
      fontWeight: '700',
      color: 'var(--text-primary)',
      marginBottom: '10px',
      lineHeight: '1.45'
    },
    typeLabel: {
      display: 'inline-block',
      fontSize: '0.65rem',
      fontWeight: '500',
      color: 'var(--text-muted)',
      textTransform: 'uppercase',
      letterSpacing: '0.03em',
      padding: '2px 6px',
      borderRadius: '3px',
      border: '1px solid var(--border)',
      marginBottom: '6px'
    },
    metaInfo: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '12px',
      marginBottom: '10px'
    },
    metaItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '5px',
      fontSize: '0.8rem',
      color: 'var(--text-muted)'
    },
    keywords: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '6px',
      marginBottom: '10px'
    },
    keyword: {
      fontSize: '0.7rem',
      color: 'var(--text-muted)',
      padding: '2px 8px',
      borderRadius: '10px',
      background: 'var(--bg-surface)',
      border: '1px solid var(--border)'
    },
    abstract: {
      fontSize: '0.875rem',
      color: 'var(--text-secondary)',
      lineHeight: '1.6',
      marginBottom: '14px',
      display: '-webkit-box',
      '-webkit-line-clamp': 3,
      '-webkit-box-orient': 'vertical',
      overflow: 'hidden'
    },
    authors: {
      fontSize: '0.825rem',
      color: 'var(--text-secondary)',
      marginBottom: '10px',
      lineHeight: '1.5'
    },
    audioToggle: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      fontSize: '0.75rem',
      color: 'var(--text-muted)',
      background: 'none',
      border: '1px solid transparent',
      cursor: 'pointer',
      padding: '6px 10px',
      borderRadius: '4px',
      marginBottom: '10px',
      transition: 'background 0.2s ease',
      ':hover': {
        background: 'var(--bg-surface)'
      }
    },
    audioSection: {
      marginBottom: '14px',
      padding: '12px',
      borderRadius: '6px',
      background: 'var(--bg-surface)',
      border: '1px solid var(--border)',
      display: 'flex',
      flexDirection: 'column',
      gap: '8px'
    },
    audioHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      color: 'var(--text-muted)',
      fontSize: '0.75rem'
    },
    audioHeaderText: {
      display: 'flex',
      flexDirection: 'column',
      fontSize: '0.75rem',
      color: 'var(--text-muted)'
    },
    audioCard: {
      background: 'var(--surface)',
      borderRadius: '6px',
      padding: '10px',
      border: '1px solid var(--border)',
      display: 'flex',
      flexDirection: 'column',
      gap: '6px'
    },
    audioMeta: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '8px',
      fontSize: '0.75rem',
      color: 'var(--text-muted)'
    },
    audioMetaRight: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      flexWrap: 'wrap'
    },
    audioDuration: {
      fontSize: '0.8rem',
      color: 'var(--text-muted)',
      display: 'flex',
      alignItems: 'center',
      gap: '4px'
    },
    audioPlayer: {
      width: '100%'
    },
    audioCaption: {
      fontSize: '0.8rem',
      color: 'var(--text-muted)'
    },
    externalLink: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      fontSize: '0.85rem',
      fontWeight: '600',
      color: 'var(--primary)',
      textDecoration: 'none'
    },
    button: {
      alignSelf: 'flex-start',
      background: 'var(--primary)',
      color: 'white !important',
      padding: '10px 20px',
      borderRadius: '8px',
      textDecoration: 'none',
      fontSize: '0.875rem',
      fontWeight: '600',
      transition: 'all 0.2s ease',
      border: 'none',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      ':hover': {
        background: 'var(--primary-dark)',
        color: 'white !important'
      }
    }
  });

  return (
    <div className={css(styles.fadeInUp)}>
      {schemaData && (
        <script type="application/ld+json">
          {JSON.stringify(schemaData)}
        </script>
      )}
      <div className={`${css(styles.publicationCard)} publication-card`} style={{ display: 'flex' }}>
        <div className={css(styles.imageSection)}>
          <img
            src={imageUrl}
            alt={`Publication: ${title}`}
            loading="lazy"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: '4px',
              filter: 'grayscale(20%)'
            }}
          />
        </div>
        <div className={css(styles.contentSection)}>
          {type && (
            <span className={css(styles.typeLabel)}>{type}</span>
          )}
          <h3 className={css(styles.title)}>{title}</h3>

          <div className={css(styles.metaInfo)}>
            {journal && (
              <div className={css(styles.metaItem)}>
                <FontAwesomeIcon icon={faBook} style={{ color: 'var(--text-muted)', fontSize: '0.7rem' }} />
                <span>{journal}</span>
              </div>
            )}
            {date && (
              <div className={css(styles.metaItem)}>
                <FontAwesomeIcon icon={faCalendarAlt} style={{ color: 'var(--text-muted)', fontSize: '0.7rem' }} />
                <span>{date}</span>
              </div>
            )}
          </div>

          {keywords && keywords.length > 0 && (
            <div className={css(styles.keywords)}>
              {keywords.map((kw, idx) => (
                <span key={idx} className={css(styles.keyword)}>{kw}</span>
              ))}
            </div>
          )}

          {authors && (
            <div className={css(styles.authors)}>
              <strong>Authors: </strong>{highlightAuthor(authors)}
            </div>
          )}

          {abstract && (
            <div className={css(styles.abstract)}>
              {abstract}
            </div>
          )}

          {podcasts?.length > 0 && (
            <>
              <button
                className={css(styles.audioToggle)}
                onClick={() => setAudioExpanded(!audioExpanded)}
                aria-expanded={audioExpanded}
              >
                <FontAwesomeIcon icon={faHeadphones} style={{ fontSize: '0.7rem' }} />
                <span>Audio summary</span>
                <FontAwesomeIcon icon={audioExpanded ? faChevronUp : faChevronDown} style={{ fontSize: '0.6rem' }} />
              </button>
              {audioExpanded && (
                <div className={css(styles.audioSection)}>
                  <div className={css(styles.audioHeader)}>
                    <span>Powered by Google Gemini</span>
                    <span style={{ marginLeft: '8px', fontSize: '0.7rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>• AI-generated — may contain errors</span>
                  </div>
                  {podcasts.map((podcast, index) => {
                    const podcastTitle = podcast.title || `Audio overview ${index + 1}`;
                    return (
                      <div key={`${podcast.url || podcast.listenUrl || 'podcast'}-${index}`}>
                        {podcast.url ? (
                      <AudioPlayer
                        src={podcast.url}
                        title={podcastTitle}
                        duration={podcast.duration}
                        downloadUrl={podcast.downloadUrl}
                        mimeType={podcast.mimeType || 'audio/mpeg'}
                      />
                    ) : podcast.listenUrl ? (
                      <a
                        href={podcast.listenUrl}
                        target="_blank"
                        rel="noreferrer"
                        className={css(styles.externalLink)}
                      >
                        <FontAwesomeIcon icon={faHeadphones} />
                        Listen externally
                      </a>
                    ) : null}
                  </div>
                );
              })}
                </div>
              )}
            </>
          )}

          <a
            href={link}
            target="_blank"
            rel="noreferrer"
            className="btn-link"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              alignSelf: 'flex-start',
              width: 'fit-content',
              gap: '0.4rem',
              padding: '0.4rem 0.85rem',
              backgroundColor: '#2563eb',
              color: '#ffffff',
              textDecoration: 'none',
              borderRadius: '5px',
              fontWeight: '600',
              fontSize: '0.8rem',
              border: 'none',
              cursor: 'pointer',
              transition: 'background-color 0.2s ease'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#1d4ed8'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#2563eb'; }}
          >
            <span className="btn-text">Read Paper</span>
            <FontAwesomeIcon icon={faExternalLinkAlt} className="btn-text" style={{ fontSize: '0.65rem' }} />
          </a>
        </div>
      </div>
    </div>
  );
}

export default Publications;
